import type { MouseEvent } from 'react';

import { useLocation } from 'react-router';

import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { pageParamKey } from '@/hooks/parent';

export interface ParentPaginationProps {
  childId: number;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
}

type PageEntry = number | 'ellipsis';

function pageWindow(current: number, totalPages: number): PageEntry[] {
  const entries: PageEntry[] = [];
  for (let page = 0; page < totalPages; page++) {
    if (page === 0 || page === totalPages - 1 || Math.abs(page - current) <= 1) {
      entries.push(page);
    } else if (entries[entries.length - 1] !== 'ellipsis') {
      entries.push('ellipsis');
    }
  }
  return entries;
}

export function ParentPagination(parentPaginationProps: ParentPaginationProps) {
  const { childId, page, pageSize, total, onPageChange, className } = parentPaginationProps;
  const location = useLocation();

  if (total <= pageSize || pageSize <= 0) return null;

  const totalPages = Math.ceil(total / pageSize);
  const current = Math.min(Math.max(page, 0), totalPages - 1);
  const isFirst = current === 0;
  const isLast = current === totalPages - 1;

  const hrefFor = (target: number): string => {
    const params = new URLSearchParams(location.search);
    if (target > 0) {
      params.set(pageParamKey(childId), String(target));
    } else {
      params.delete(pageParamKey(childId));
    }
    const query = params.toString();
    return query ? `${location.pathname}?${query}` : location.pathname;
  };

  const handleClick = (target: number) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onPageChange(target);
  };

  return (
    <Pagination aria-label={'Paginacja'} className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            text={'Poprzednia'}
            aria-label={'Przejdź do poprzedniej strony'}
            href={isFirst ? undefined : hrefFor(current - 1)}
            onClick={isFirst ? undefined : handleClick(current - 1)}
            aria-disabled={isFirst}
            tabIndex={isFirst ? -1 : undefined}
            className={isFirst ? 'pointer-events-none opacity-50' : undefined}
          />
        </PaginationItem>

        {pageWindow(current, totalPages).map((entry, index) => (
          <PaginationItem key={entry === 'ellipsis' ? `ellipsis-${index}` : entry}>
            {entry === 'ellipsis' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink href={hrefFor(entry)} onClick={handleClick(entry)} isActive={entry === current} aria-label={`Przejdź do strony ${entry + 1}`}>
                {entry + 1}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            text={'Następna'}
            aria-label={'Przejdź do następnej strony'}
            href={isLast ? undefined : hrefFor(current + 1)}
            onClick={isLast ? undefined : handleClick(current + 1)}
            aria-disabled={isLast}
            tabIndex={isLast ? -1 : undefined}
            className={isLast ? 'pointer-events-none opacity-50' : undefined}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
